/**
 * TypeScript types for Multi-Tenant System
 */

/**
 * Tenant Type Enum
 * Defines the type of tenant and its role in the platform
 */
export enum TenantType {
  MASTER = 'MASTER',
  ACCELERATOR_APP = 'ACCELERATOR_APP',
  REGULAR = 'REGULAR',
}

/**
 * Tenant Feature Enum
 * Defines features that can be enabled/disabled per tenant
 */
export enum TenantFeature {
  TENANT_MANAGEMENT = 'TENANT_MANAGEMENT',
  INCUBATORS = 'INCUBATORS',
  USER_MANAGEMENT = 'USER_MANAGEMENT',
  SUBSCRIPTIONS = 'SUBSCRIPTIONS',
  ORGANIZATIONS = 'ORGANIZATIONS',
  ANALYTICS = 'ANALYTICS',
  FEATURE_FLAGS = 'FEATURE_FLAGS',
  PRODUCT_CONFIG = 'PRODUCT_CONFIG',
  MARKET_CONFIG = 'MARKET_CONFIG',
  PRICING = 'PRICING',
  FINANCE = 'FINANCE',
  IMPORT = 'IMPORT',
  /** Enable automatic subscription renewal processing */
  AUTO_RENEWAL = 'AUTO_RENEWAL',
  /** Enable grace period for expired subscriptions */
  GRACE_PERIOD = 'GRACE_PERIOD',
  /**
   * CRM & Tasks: prospects, contacts, pipeline, and the tasks workspace.
   * Gates the CRM/Tasks nav entries and routes (backend gates the matching
   * resolvers with the same flag).
   */
  CRM = 'CRM',
  /**
   * Billing schedules: the prepaid-term and deferred-activation presets in the
   * create wizard, the Activate action, and the pending-activations queue.
   * The backend gates the matching resolvers with the same flag and refuses a
   * preset outright on a workspace without it.
   */
  BILLING_SCHEDULE = 'BILLING_SCHEDULE',
  /**
   * OKR management: the Strategy section and the target overlay on analytics
   * charts. A priced add-on, so this flag is the commercial gate — but the
   * tenant must also activate the module, which needs a connected Slack
   * workspace. Entitled-but-not-activated is a real state the UI must render.
   */
  OKR = 'OKR',
  /**
   * Tier 1 email triage: the AI classifier that resolves what rule-based
   * pre-filtering escalates. Per-tenant pilot switch on top of a
   * deployment-wide spend ceiling — granting it does nothing until the
   * backend's AI tier pricing is configured.
   */
  EMAIL_TIER1_TRIAGE = 'EMAIL_TIER1_TRIAGE',
  /**
   * Tier 2 commitment extraction: reads Tier 1-qualified email for
   * commitments and fires overdue-commitment situations. Per-tenant pilot
   * switch on top of a deployment-wide spend ceiling, same shape as
   * EMAIL_TIER1_TRIAGE and independent of it.
   */
  EMAIL_TIER2_EXTRACTION = 'EMAIL_TIER2_EXTRACTION',
  /**
   * Revvly Support: tickets, live chat, the AI support agent and the public
   * chat API. A priced add-on, granted per client.
   */
  SUPPORT = 'SUPPORT',
  /**
   * Revvly Forms: form builder, hosted and embedded public forms, stored
   * submissions and the action sequences each submission runs.
   */
  FORMS = 'FORMS',
  /**
   * Daily Check-ins: Slack DMs on working days and a compiled report in a
   * channel. A flat per-company add-on, granted per client.
   */
  CHECKINS = 'CHECKINS',
  /**
   * Tax management: registrations, tenant-owned rates, customer tax profiles
   * and per-line tax on invoices.
   */
  TAX = 'TAX',
  /**
   * Work Inbox: each person's own mail, sorted into categories at ingest, at
   * /inbox. Every tenant gets it; the flag only stages the rollout.
   */
  WORK_INBOX = 'WORK_INBOX',
  /**
   * Assistant-led onboarding: an open onboarding runs in the full-page
   * assistant instead of the setup wizard. A rollout flag, not a paid tier.
   */
  ASSISTANT_ONBOARDING = 'ASSISTANT_ONBOARDING',
  /**
   * Google Drive client folders: one Drive folder per client and prospect,
   * kept in sync with the files Revvly holds for that company. Needs a Drive
   * connection with write access, set up in Settings → Integrations.
   */
  DRIVE_FOLDERS = 'DRIVE_FOLDERS',
  PRODUCT_USAGE = 'PRODUCT_USAGE',
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  description?: string;
  contactEmail?: string;
  contactName?: string;
  isActive: boolean;
  type: TenantType;
  features: TenantFeature[];
  isMandatory: boolean;
  /** Set only for self-serve workspaces; null means unrestricted. */
  billingStatus?: 'TRIALING' | 'ACTIVE' | 'PAST_DUE' | 'SUSPENDED' | 'CANCELLED' | 'BLOCKED' | null;
  createdAt: Date;
  updatedAt: Date;
  invoiceHeader?: {
    logoUrl?: string | null;
  } | null;
}

export interface UserTenant {
  id: string;
  userId: string;
  tenantId: string;
  role: string;
  permissions: string[];
  isActive: boolean;
  invitedBy?: string;
  invitedAt?: Date;
  lastAccessedAt?: Date;
  tenant: Tenant;
  createdAt: Date;
  updatedAt: Date;
}

export interface TenantContextType {
  selectedTenant: Tenant | null;
  availableTenants: UserTenant[];
  selectTenant: (tenantId: string) => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  loadTenants: () => Promise<UserTenant[]>;
  restoreTenantContext: (tenantId: string, retryCount?: number) => Promise<boolean>;
  isLoading: boolean;
  isRestoringContext: boolean;
  error: Error | null;
}

export interface SelectTenantInput {
  tenantId: string;
}

export class TenantError extends Error {
  public constructor(
    message: string,
    public code: string,
  ) {
    super(message);
    this.name = 'TenantError';
  }
}
